import {selx} from 'clsx';
import {AttrsSchema, Component, jsx} from 'panel';
import type {VNode} from 'snabbdom';

import {FeatureGate, ReportName} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {ChartHeaderEl, ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {ReportStatusHero} from 'common/widgets/warning-hero/presets';
import {ReportStatus, WarningHeroReportName} from 'common/widgets/warning-hero/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from 'common/report/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {ResizingLayoutAttrs, ResizingLayoutEl} from 'common/widgets/resizing-layout';
import {UserDetails} from 'common/report/stores/user-details';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {
  SegmentFilterDataTableEl,
  SegmentFilterDataTableJsxProps,
} from 'common/charts/misc/segment-filter-data-table/index';
import {propagateEvent} from 'common/util/dom';
import {SortChangeEvent} from 'common/components/data-table/types';
import {ReportLayout} from 'common/report/contexts/report-layout/types';
import {ReportSwitchEvent} from 'common/query-builder/query-builder/types';

import {BookmarkUpdateDetail, BookmarkUpdateEvent, RequestOdaEvent} from '../types';
import styles from './index.module.styl';

export type ReportVisualizationAttrs = {
  status?: ReportStatus;
  'is-download-mode': boolean;
  'show-resizing-layout': boolean;
  'has-data-table': boolean;
  'project-id': number;
  'workspace-id'?: number;
  'rca-source-id'?: number;
  'show-query-sampling-cta': boolean;
  'show-mp-tips': boolean;
};

export type ReportVisualizationProps<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> = {
  paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>;
  error: QueryErrorType;
  project: CurrentProject;
  dataGroups?: DataGroup[];
  customEvents: CustomEventDefinition[];
  userDetails: UserDetails;
};

export type VisualizationEventHandlers = {
  update: (ev: BookmarkUpdateEvent) => void;
  'export-csv'?: (ev: CustomEvent<void>) => void;
  'request-oda'?: (ev: RequestOdaEvent) => void;
  'toggle-query-sampling': (ev: CustomEvent<{on: boolean}>) => void;
  reportswitch?: (ev: ReportSwitchEvent) => void;
};

export abstract class ReportVisualization<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> extends Component<any, ReportVisualizationAttrs, any, any, IronContextRegistry> {
  abstract chartDisplay(): VNode;
  abstract reportName: ReportName;

  static get attrsSchema(): AttrsSchema<ReportVisualizationAttrs> {
    return {
      ...super.attrsSchema,
      status: {type: `string`, tsType: `ReportStatus`},
      'is-download-mode': `boolean`,
      'has-data-table': `boolean`,
      'show-resizing-layout': `boolean`,
      'project-id': `number`,
      'workspace-id': `number`,
      'show-query-sampling-cta': `boolean`,
      'show-mp-tips': `boolean`,
      'rca-source-id': `number`,
    };
  }

  get defaultState() {
    return {};
  }

  get config() {
    return {
      defaultState: this.defaultState,
      contexts: [
        `localStorageManager` as const,
        `queryContextManager` as const,
        `reportLayoutManager` as const,
        `segmentFilterController` as const,
        `urlManager` as const,
      ],
      template: () => {
        const chartDisplay = (
          <div sel={selx(styles.reportVizChart)} attrs={{slot: `report-viz`}}>
            {this.attr(`status`) ? this.reportStatusTemplate() : this.chartDisplay()}
          </div>
        );

        if (this.attr(`is-download-mode`)) {
          return chartDisplay;
        }

        const chartHeader = (
          <div sel={selx(styles.reportVizChartHeader)} attrs={{slot: `report-controls`}}>
            <ChartHeaderEl
              attrs={{...this.headerAttrs(), elref: `chartHeader`}}
              on={{
                update: (ev) => this.handleHeaderUpdate(ev),
              }}
            >
              {this.headerChildrenTemplate()}
            </ChartHeaderEl>
          </div>
        );

        const fullSizeTableLayout =
          this.getContext(`reportLayoutManager`).getReportLayout() === ReportLayout.SplitTableOnly;
        const showDataTable = (!this.attr(`status`) || fullSizeTableLayout) && this.attr(`has-data-table`);
        const renderStatusInTable = this.attr(`status`) && fullSizeTableLayout;

        const dataTableContainerTemplate = showDataTable ? (
          <div sel={selx(styles.reportVizTable)} attrs={{slot: `table`}}>
            {renderStatusInTable ? this.reportStatusTemplate() : this.dataTableContainerTemplate()}
          </div>
        ) : null;

        return (
          <div sel={selx(styles.reportVizWrapper)} class={{[styles.resizingLayout]: this.attr(`show-resizing-layout`)}}>
            <div sel={selx(styles.reportVizResults)}>
              <ResizingLayoutEl
                attrs={{
                  'show-data-table': showDataTable,
                  'show-resizing-layout': this.attr(`show-resizing-layout`),
                  ...this.reportSpecificResizingLayoutAttrs(),
                }}
              >
                {chartHeader}
                {chartDisplay}
                {dataTableContainerTemplate}
              </ResizingLayoutEl>
            </div>
          </div>
        );
      },
    };
  }

  private reportStatusTemplate() {
    return (
      <div sel={selx(styles.reportVizStatus)}>
        <ReportStatusHero
          queryError={this.error}
          reportName={this.reportName as WarningHeroReportName}
          reportStatus={this.attr(`status`)}
          showQuerySamplingCTA={this.attr(`show-query-sampling-cta`)}
          showMpTips={this.attr(`show-mp-tips`)}
          enableQuerySampling={(ev) => {
            this.dispatchEvent(new CustomEvent(`toggle-query-sampling`, {detail: ev.detail}));
          }}
        />
      </div>
    );
  }

  headerAttrs(): Partial<ChartHeaderAttrs> {
    if (!this.bookmarkParams || !this.project) {
      return {};
    }

    return {
      'max-data-history-days': this.project.getFeatureGate(FeatureGate.MaxDataHistoryDays),
      ...(this.project.projectAsOfDate ? {'project-as-of-date': this.project.projectAsOfDate} : null),
      'project-id': this.project.id,
      'track-name': this.reportName,

      ...this.reportSpecificHeaderAttrs(),
    };
  }

  private handleHeaderUpdate(ev: CustomEvent<ChartHeaderUpdateDetail>) {
    const detail = this.bookmarkUpdateFromHeaderUpdate(ev.detail);
    if (detail.params) {
      this.dispatchUpdate(detail);
    }
  }

  // allow reports to dispatch bookmark updates from chart interactions, legend selections etc
  dispatchUpdate({
    params,
    queryReason = QueryReason.QbOtherUpdate,
    requery = true,
    hasChangedChartType = false,
  }: BookmarkUpdateDetail): void {
    const event: BookmarkUpdateEvent = new CustomEvent(`update`, {
      detail: {params, queryReason, requery, hasChangedChartType},
    });
    this.dispatchEvent(event);
  }

  abstract bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail): BookmarkUpdateDetail<BookmarkParamsType>;

  // to be overridden by reports that need slotted content
  protected headerChildrenTemplate() {
    return null;
  }

  protected reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    return {};
  }

  /** Additional attrs to pass to ResizingLayoutEl, e.g. to remove padding when the table is rendered in the
   * chart slot (insights table chart) */
  protected reportSpecificResizingLayoutAttrs(): Partial<ResizingLayoutAttrs> {
    return {};
  }

  // renders a data table when this.attr(`has-data-table`) is true
  private dataTableContainerTemplate() {
    const footerTemplate = this.dataTableFooterTemplate();

    return (
      <div sel={selx(styles.chartTableContainer)}>
        <div sel={selx(styles.chartTable)}>{this.dataTableTemplate()}</div>
        {footerTemplate && <div sel={selx(styles.chartTableFooter)}>{footerTemplate}</div>}
      </div>
    );
  }

  protected dataTableTemplate() {
    return (
      <SegmentFilterDataTableEl
        attrs={{
          ...this.reportSpecificDataTableAttrs(),
          'resizing-layout': this.attr(`show-resizing-layout`),
          elref: `dataTable`,
        }}
        on={{
          'export-csv': (ev) => propagateEvent(this, ev),
          sortChange: (ev: SortChangeEvent) => {
            this.dispatchUpdate({
              params: this.getBookmarkParamsFromSortChange(ev),
              requery: false,
            });
          },
        }}
      />
    );
  }

  protected reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    return null;
  }

  protected getBookmarkParamsFromSortChange(_ev: SortChangeEvent): BookmarkParamsType {
    return this.bookmarkParams;
  }

  // to be overriden for reports that have a custom footer (e.g. ODA)
  protected dataTableFooterTemplate(): VNode {
    return null;
  }

  set paramsAndResults(
    value: ReportVisualizationProps<BookmarkParamsType, QueryResultsType, QueryErrorType>['paramsAndResults'],
  ) {
    this._results = value.results;
    this._bookmarkParams = value.params;
    this.update();
  }

  private _bookmarkParams: BookmarkParamsType;
  get bookmarkParams() {
    return this._bookmarkParams;
  }

  private _results: QueryResultsType;
  get results() {
    return this._results;
  }

  private _error: QueryErrorType;
  get error() {
    return this._error;
  }
  set error(error: QueryErrorType) {
    this._error = error;
    this.update();
  }

  private _project: CurrentProject;
  get project() {
    return this._project;
  }
  set project(project: CurrentProject) {
    this._project = project;
    this.update();
  }

  private _dataGroups: DataGroup[];
  get dataGroups() {
    return this._dataGroups;
  }
  set dataGroups(dataGroups: DataGroup[]) {
    this._dataGroups = dataGroups;
    this.update();
  }

  private _customEvents: CustomEventDefinition[];
  get customEvents() {
    return this._customEvents;
  }
  set customEvents(customEvents: CustomEventDefinition[]) {
    this._customEvents = customEvents;
    this.update();
  }

  private _userDetails: UserDetails;
  get userDetails() {
    return this._userDetails;
  }
  set userDetails(userDetails: UserDetails) {
    this._userDetails = userDetails;
    this.update();
  }
}
